/**
 * The home page's enrolment strip and the dialog its CTA opens.
 *
 * Composed the way the library's own reference host does it (apps/demo/src/join.tsx): JoinModal is
 * fully controlled -- it holds no open state of its own -- so the single piece of UI state in this
 * feature lives here.
 *
 * Both components are static copy with shipped defaults and fetch nothing, so nothing in this tree
 * has a loading state. The one read is the enrolment status, and it decides whether the strip is
 * ever SEEN rather than what it renders -- which is why it is handed to the reveal rather than
 * held here. The strip is not an ACF block either: the front page never calls the_content(), so
 * its markup is a Blade partial that borrows this bundle's mount. See
 * resources/views/partials/loyalty-join-strip.blade.php.
 */

import { useEffect, useState } from 'preact/hooks';
import { JoinModal, LoyaltyJoin, useLoyaltyClient } from '@drewling/urc-loyalty-ui';
import { initJoinStripReveal } from '../joinStripReveal';
import '../joinStrip.css';

/**
 * @param {Object}  props
 * @param {string}  props.joinUrl      Target of the dialog's "Join Loyalty" button.
 * @param {string=} props.learnMoreUrl Target of the "Learn More" row, or undefined for no row.
 */
export function JoinStripBlock( { joinUrl, learnMoreUrl } ) {
	const client = useLoyaltyClient();
	const [ open, setOpen ] = useState( false );

	useEffect( () => {
		// After the first render, so the collapsed box already has content to open onto. The
		// reveal owns the cookie gate and the animation; this callback is the only part that
		// knows about enrolment, and it is invoked only once the cookie says there is a fan to
		// ask about. The returned teardown drops the auth subscription and the resize listener —
		// it deliberately does not re-close a strip that has opened.
		return initJoinStripReveal( () =>
			client
				.getEnrolmentStatus()
				.then( status => status.state )
				// A throw is not a fourth state. getEnrolmentStatus resolves 'unconfigured' by
				// itself -- it catches the route's own 503 and maps it -- so what reaches here is
				// a real failure: the empty-token LoyaltyAuthError raised before the wire, a
				// timeout, a 5xx. All of them mean the same thing, which is that we cannot tell.
				.catch( () => 'unknown' )
		);
	}, [ client ] );

	return (
		<>
			{ /*
				onCtaClick and no ctaHref: LoyaltyJoin renders an <a> when given an href and a
				<button> when given only a handler. This control opens a dialog rather than
				navigating, so a button is the honest element.

				No wrapper and no padding around this. The strip is full-bleed by design and its
				own Container supplies the 48px/20px side margins; nesting it in a padded box
				would inset it twice.
			*/ }
			<LoyaltyJoin onCtaClick={ () => setOpen( true ) } />
			{ /*
				learnMoreUrl arrives as undefined when the editor left the field blank --
				App\Loyalty\JoinStrip omits the key rather than sending '' (D-039) -- and the
				library's TertiaryLink returns null when given neither an href nor an onClick, so
				the row disappears rather than rendering a dead link.

				No `labels`: every string on both surfaces ships a library default, and none of
				them is editable in this feature.
			*/ }
			<JoinModal
				open={ open }
				onClose={ () => setOpen( false ) }
				joinHref={ joinUrl }
				learnMoreHref={ learnMoreUrl }
			/>
		</>
	);
}
